import { record } from 'rrweb';
import { api, setAssisting, type LiveAction, type LiveControl, type LiveStatus } from '../api/client';

/**
 * The customer's side of live help, loaded only once they say yes: sends a live copy of this tab
 * (rrweb) to Stitch support, and carries out what support does. Pointing is always shown; clicks,
 * typing and scrolling only while the customer has given control, and never on live help's own
 * controls (so support can't say yes for them), password fields or anything marked private.
 */

export interface ShareEvents {
  status(s: { status: LiveStatus; control: LiveControl }): void;
  pointer(p: { x: number; y: number } | null): void;
  ping(p: { x: number; y: number }): void;
  ended(): void;
}

/** Live help's own controls, and anything private, are never sent and never driven. */
export const PRIVATE = '[data-private], [data-live-ui]';

const TEXT_TYPES = new Set(['', 'text', 'search', 'email', 'tel', 'url', 'number']);

export function startSharing(id: string, tabId: string, on: ShareEvents): () => Promise<void> {
  // A session recording steps aside first: live help is never recorded.
  window.dispatchEvent(new CustomEvent('stitch:live-share', { detail: { on: true } }));
  let stopped = false;
  let control: LiveControl = 'none';
  let queue: unknown[] = [];
  let sending = false;
  let timer: number | undefined;
  let failures = 0;
  /** The batch on its way, so stopping can wait for it before the session is ended. */
  let inflight: Promise<unknown> = Promise.resolve();

  const flush = async () => {
    timer = undefined;
    if (stopped || sending || !queue.length) return;
    sending = true;
    const batch = queue;
    queue = [];
    try {
      const sent = api.liveEvents(id, tabId, batch);
      inflight = sent.catch(() => undefined);
      await sent;
      failures = 0;
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 410 || status === 404 || status === 409 || status === 401) {
        stop();
        on.ended();
        return;
      }
      // A blip: keep what didn't send, unless it's grown too big, in which case start afresh.
      failures++;
      queue = batch.concat(queue);
      if (queue.length > 3000) {
        queue = [];
        record.takeFullSnapshot(true);
      }
    } finally {
      sending = false;
    }
    if (queue.length) schedule(failures ? Math.min(5000, 500 * 2 ** failures) : 150);
  };
  const schedule = (ms = 150) => {
    if (timer === undefined && !stopped) timer = window.setTimeout(() => void flush(), ms);
  };

  const stopRecording = record({
    emit(e) {
      queue.push(e);
      schedule();
    },
    blockSelector: PRIVATE,
    maskInputOptions: { password: true },
    inlineStylesheet: true,
    // A fresh full copy every few minutes keeps what the server holds (and the viewer replays) small.
    checkoutEveryNms: 4 * 60_000,
    sampling: { mousemove: 60, scroll: 120, input: 'last' },
    recordCanvas: false,
    collectFonts: false
  });

  const events = new EventSource(`/api/live/sessions/${encodeURIComponent(id)}/stream?tab=${encodeURIComponent(tabId)}`);
  events.addEventListener('status', (e) => {
    const s = JSON.parse((e as MessageEvent<string>).data) as { status: LiveStatus; control: LiveControl };
    control = s.control;
    setAssisting(s.status === 'live' && s.control === 'granted' ? id : null);
    on.status(s);
    if (s.status === 'ended') {
      stop();
      on.ended();
    }
  });
  events.addEventListener('snapshot', () => record.takeFullSnapshot(true));
  events.addEventListener('act', (e) => {
    try {
      act(JSON.parse((e as MessageEvent<string>).data) as LiveAction);
    } catch {
      // One bad action never stops the session.
    }
  });
  events.addEventListener('error', () => {
    // The browser reconnects by itself; a closed stream means the session is over (or we're signed out).
    if (events.readyState === EventSource.CLOSED && !stopped) {
      stop();
      on.ended();
    }
  });

  function act(a: LiveAction): void {
    if (a.type === 'pointer') return on.pointer({ x: a.x, y: a.y });
    if (a.type === 'pointer-out') return on.pointer(null);
    if (a.type === 'ping') return on.ping({ x: a.x, y: a.y });
    if (control !== 'granted') return;
    if (a.type === 'click') return click(a.node, a.x, a.y);
    if (a.type === 'text') return typeText(a.text);
    if (a.type === 'key') return press(a.key, Boolean(a.shift));
    if (a.type === 'scroll') return scroll(a.node, a.dx, a.dy);
    if (a.type === 'choose') return choose(a.node, a.value);
  }

  function stop(): Promise<void> {
    if (stopped) return inflight.then(() => undefined);
    stopped = true;
    window.clearTimeout(timer);
    stopRecording?.();
    events.close();
    setAssisting(null);
    control = 'none';
    window.dispatchEvent(new CustomEvent('stitch:live-share', { detail: { on: false } }));
    return inflight.then(() => undefined);
  }

  return stop;
}

// --- carrying out support's actions ---------------------------------------------------------------

function elementOf(id: number): HTMLElement | null {
  const n = record.mirror.getNode(id) as Node | null;
  let el: Element | null = n && n.nodeType === Node.ELEMENT_NODE ? (n as Element) : (n?.parentElement ?? null);
  // An icon's SVG (or a path inside it) isn't an HTML element: the button around it is, so a click
  // right on the icon of a round icon-only button presses the button (3043).
  while (el && !(el instanceof HTMLElement)) el = el.parentElement;
  return el && el.isConnected ? (el as HTMLElement) : null;
}

/** Whether support may act on this element. */
export function drivable(el: Element | null): el is HTMLElement {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.closest(PRIVATE)) return false;
  if (el instanceof HTMLInputElement && (el.type === 'password' || el.type === 'file')) return false;
  const link = el.closest('a');
  // Links that leave Stitch, or open somewhere else, are the customer's to follow.
  if (link && (link.target === '_blank' || (link.href && new URL(link.href, location.href).origin !== location.origin))) return false;
  return true;
}

function textField(el: Element | null): el is HTMLInputElement | HTMLTextAreaElement {
  if (!drivable(el)) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  return el instanceof HTMLInputElement && TEXT_TYPES.has(el.type) && !el.readOnly && !el.disabled;
}

function setValue(el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, value: string): void {
  // React keeps its own copy of a field's value: set it the way the browser would, then say so.
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : el instanceof HTMLSelectElement ? HTMLSelectElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, value);
}

function edit(el: HTMLInputElement | HTMLTextAreaElement, text: string, remove?: 'back' | 'forward'): void {
  const v = el.value;
  let start = v.length;
  let end = v.length;
  try {
    start = el.selectionStart ?? v.length;
    end = el.selectionEnd ?? v.length;
  } catch {
    // Number and email fields have no selection: add at the end.
  }
  if (remove === 'back' && start === end) start = Math.max(0, start - 1);
  if (remove === 'forward' && start === end) end = Math.min(v.length, end + 1);
  const next = v.slice(0, start) + text + v.slice(end);
  if (el.maxLength > 0 && next.length > el.maxLength) return;
  setValue(el, next);
  try {
    el.setSelectionRange(start + text.length, start + text.length);
  } catch {
    // As above.
  }
  el.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: remove ? (remove === 'back' ? 'deleteContentBackward' : 'deleteContentForward') : 'insertText', data: text || null }));
}

function mouse(el: HTMLElement, type: string, x: number, y: number): boolean {
  const init = { bubbles: true, cancelable: true, composed: true, clientX: x, clientY: y, view: window, button: 0, buttons: type.endsWith('down') ? 1 : 0, detail: 1 };
  const e = type.startsWith('pointer') ? new PointerEvent(type, { ...init, pointerId: 77, pointerType: 'mouse', isPrimary: true }) : new MouseEvent(type, init);
  return el.dispatchEvent(e);
}

function click(node: number, x: number, y: number): void {
  const el = elementOf(node);
  if (!drivable(el)) return;
  mouse(el, 'pointerdown', x, y);
  const go = mouse(el, 'mousedown', x, y);
  if (go) {
    const focusable = el.closest<HTMLElement>('input, textarea, select, button, a[href], [tabindex]');
    if (focusable && drivable(focusable)) focusable.focus({ preventScroll: true });
    else if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) document.activeElement.blur();
  }
  mouse(el, 'pointerup', x, y);
  mouse(el, 'mouseup', x, y);
  mouse(el, 'click', x, y);
}

function typeText(text: string): void {
  const el = document.activeElement;
  if (textField(el)) return edit(el, el instanceof HTMLInputElement ? text.replace(/[\r\n]+/g, ' ') : text);
  if (text === ' ' && drivable(el) && el !== document.body) el.click();
}

function focusables(): HTMLElement[] {
  // Inside an open dialog, Tab stays in the top one, as it does for the customer.
  const scrims = document.querySelectorAll('.scrim');
  const root: ParentNode = scrims.length ? scrims[scrims.length - 1]! : document;
  return [...root.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])')].filter(
    (el) => drivable(el) && el.offsetParent !== null
  );
}

function press(key: string, shift: boolean): void {
  const active = document.activeElement instanceof HTMLElement ? document.activeElement : document.body;
  if (active !== document.body && !drivable(active)) return;
  const init = { key, code: key === ' ' ? 'Space' : key, bubbles: true, cancelable: true, composed: true, shiftKey: shift };
  const go = active.dispatchEvent(new KeyboardEvent('keydown', init));
  if (go) {
    if (key === 'Tab') {
      const list = focusables();
      const i = list.indexOf(active);
      const next = list[(i + (shift ? -1 : 1) + list.length) % list.length];
      next?.focus();
    } else if (key === 'Backspace' && textField(active)) edit(active, '', 'back');
    else if (key === 'Delete' && textField(active)) edit(active, '', 'forward');
    else if (key === 'Enter') {
      if (active instanceof HTMLTextAreaElement && textField(active)) edit(active, '\n');
      else if (active instanceof HTMLInputElement && active.form && textField(active)) active.form.requestSubmit();
      else if (active instanceof HTMLButtonElement || active instanceof HTMLAnchorElement) active.click();
    } else if (key === ' ') typeText(' ');
    else if ((key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Home' || key === 'End') && textField(active)) {
      try {
        const at = active.selectionStart ?? 0;
        const to = key === 'Home' ? 0 : key === 'End' ? active.value.length : Math.max(0, Math.min(active.value.length, at + (key === 'ArrowLeft' ? -1 : 1)));
        active.setSelectionRange(to, to);
      } catch {
        // No caret in this kind of field.
      }
    }
  }
  active.dispatchEvent(new KeyboardEvent('keyup', init));
}

function scroll(node: number | null, dx: number, dy: number): void {
  let el: HTMLElement | null = node ? elementOf(node) : null;
  if (el && el.closest(PRIVATE)) return;
  // The nearest part of the page that scrolls that way, else the page itself.
  while (el && el !== document.body) {
    const style = getComputedStyle(el);
    const canY = dy !== 0 && /(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight;
    const canX = dx !== 0 && /(auto|scroll)/.test(style.overflowX) && el.scrollWidth > el.clientWidth;
    if (canY || canX) {
      el.scrollBy({ left: dx, top: dy });
      return;
    }
    el = el.parentElement;
  }
  window.scrollBy({ left: dx, top: dy });
}

function choose(node: number, value: string): void {
  const el = elementOf(node);
  if (!(el instanceof HTMLSelectElement) || !drivable(el) || el.disabled) return;
  if (![...el.options].some((o) => o.value === value && !o.disabled)) return;
  el.focus({ preventScroll: true });
  setValue(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
}
